import { api } from '@/shared/services/baseApiClient';
import { config } from '@/config/config';
import urljoin from 'url-join';
import {
	BotConfigurationViewModel,
	BusinessHoursViewModel,
} from '@/features/bots/models';
import { InstallBotViewModel } from '@/features/bots/models';
import { UpdateBotViewModel } from '@/features/bots/models';
import { AppConfigViewModel } from '@/features/apps/shared/models';
import { AppInitViewModel } from '@/features/apps/shared/models/appInitViewModel';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import { ExecuteTestBotViewModel } from '@/features/bots/models/request/executeTestBotViewModel';
import { BotTestResponseViewModel } from '@/features/bots/models/botTestResponseViewModel';
import { BotAppInitViewModel } from '../models/request/botAppInitViewModel';
import { BotAppSubmitViewModel } from '@/features/bots/models/request/botAppSubmitViewModel';
import { AppSubmitViewModel } from '@/features/apps/shared/models/appSubmitViewModel';

class BotsApiService {
	private version = 'v1';

	public install(
		request: InstallBotViewModel
	): Promise<BotConfigurationViewModel> {
		return api.send(
			'POST',
			urljoin(this.host, this.version, 'app/install'),
			request
		);
	}

	public update(workspaceId: string, bot: BotConfigurationViewModel) {
		const request = bot as UpdateBotViewModel;
		request.workspaceId = workspaceId;
		return api.send(
			'POST',
			urljoin(this.host, this.version, 'app/update'),
			bot
		);
	}

	public get(workspaceId: string): Promise<BotConfigurationViewModel> {
		return api.send(
			'GET',
			urljoin(this.host, this.version, `app?workspaceId=${workspaceId}`)
		);
	}

	public async botAppConfig(
		workspaceId: string,
		installedAppId: string,
		inputData: { [key: string]: string },
		storedData: { [key: string]: string },
		componentId?: string
	): Promise<AppConfigViewModel> {
		const request = {
			inputData,
			storedData,
			componentId,
		};
		const url = urljoin(
			this.host,
			this.version,
			'workspaces',
			workspaceId,
			'apps',
			installedAppId,
			'config'
		);
		const response: any = await api.send('POST', url, request);
		if (typeof response.panel === 'string') {
			response.panel = {
				components: JSON.parse(response.panel),
				storedData: {},
			};
		}
		return response;
	}

	public async botAppInit(workspaceId: string, installedAppId: string, request: BotAppInitViewModel): Promise<AppInitViewModel> {
		const url = urljoin(this.host, this.version, 'workspaces', workspaceId, 'apps', installedAppId, 'init');
		const response: any = await api.send('POST', url, request);
		if (typeof response.panel === 'string') {
			response.panel = {
				components: JSON.parse(response.panel),
				storedData: {},
			};
		}
		return response;
	}

	public async botAppSubmit(workspaceId: string, installedAppId: string, request: BotAppSubmitViewModel): Promise<AppSubmitViewModel> {
		const url = urljoin(this.host, this.version, 'workspaces', workspaceId, 'apps', installedAppId, 'submit');
		return await api.send('POST', url, request);
	}

	public async getHolidays(botId: string): Promise<HolidayViewModel[]> {
		const url = urljoin(
			this.host,
			this.version,
			'bots',
			botId,
			'holidays'
		);
		const holidays: HolidayViewModel[] = await api.send('GET', url);
		return holidays.map(this.convertIso8601ToDate);
	}

	public async createHoliday(
		botId: string,
		request: HolidayViewModel
	): Promise<HolidayViewModel> {
		const url = urljoin(
			this.host,
			this.version,
			'bots',
			botId,
			'holidays'
		);
		const holiday: HolidayViewModel = await api.send('POST', url, request);
		return this.convertIso8601ToDate(holiday);
	}

	public async updateHoliday(
		botId: string,
		request: HolidayViewModel
	): Promise<HolidayViewModel> {
		const url = urljoin(
			this.host,
			this.version,
			'bots',
			botId,
			'holidays',
			request.id!
		);
		const holiday: HolidayViewModel = await api.send('PUT', url, request);
		return this.convertIso8601ToDate(holiday);
	}

	public deleteHoliday(
		botId: string,
		holidayId: string
	): Promise<void> {
		const url = urljoin(
			this.host,
			this.version,
			'bots',
			botId,
			'holidays',
			holidayId
		);
		return api.send('DELETE', url, holidayId);
	}

	public getBusinessHours(
		workspaceId: string
	): Promise<BusinessHoursViewModel[]> {
		const url = urljoin(
			this.host,
			this.version,
			'bots',
			workspaceId,
			'businesshours'
		);
		return api.send('GET', url);
	}

	public updateBusinessHours(
		workspaceId: string,
		businessHours: BusinessHoursViewModel[]
	) {
		const url = urljoin(
			this.host,
			this.version,
			'bots',
			workspaceId,
			'businesshours'
		);
		return api.send('POST', url, businessHours);
	}

	public sendTestBotEvent(
		workspaceId: string,
		request: ExecuteTestBotViewModel
	): Promise<BotTestResponseViewModel> {
		return api.send('POST', urljoin(this.host, this.version, 'bots', workspaceId, 'test'), request);
	}

	public deleteSession(
		workspaceId: string,
		sessionId: string
	): Promise<void> {
		return api.send('DELETE', urljoin(this.host, this.version, 'bots', workspaceId, 'sessions', sessionId));
	}

	private get host() {
		return config().botsHost;
	}

	private convertIso8601ToDate(holiday: HolidayViewModel) {
		holiday.dates = holiday.dates.map(
			(iso8601) => new Date(Date.parse(String(iso8601)))
		);
		return holiday;
	}
}

export const botsApi = new BotsApiService();
